import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Corpus } from '../../../models/corpus';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, HelpIndex, MessageType, UpdateStatusType } from '../../../models/enums';
import { GenericColDef, GridRowSelectionType } from '../../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';

@Component({
    standalone: false,
    selector: 'res-add-shared-data-set-modal',
    templateUrl: './add-shared-data-set-modal.component.html',
    styleUrls: ['./add-shared-data-set-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AddSharedDataSetModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @ViewChild('successPop', { static: true }) successPop: PopoverDirective;
    @ViewChild('errorPop', { static: true }) errorPop: PopoverDirective;

    addToProjectData: boolean;
    validateOK = false;

    // modal inputs
    modalTitle = 'Add Shared Data Set';
    modalAutomationId = 'add-shared-data-set-modal-container';
    verifyRequiredValidator = true;
    primaryButtonLabel = 'Add Data Set(s)';
    secondaryButtonLabel = 'Cancel';
    modalClass = 'add-shared-data-set';
    modalHelpIndex = HelpIndex.imports_add_shared_data_set_dlg;
    isBusy = false;
    isBusyMessage: string;
    hasDisabledRow = false;

    // permissions
    hasAddToProjectPermissions = true;

    // generic grid inputs
    updateStatusType = UpdateStatusType.CORPUS;
    columnDefs = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            automationId: this.modalAutomationId,
            minWidth: 36, maxWidth: 36, width: 36,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            suppressSizeToFit: true,
            lockVisible: true,
            tooltipValueGetter: (params) => {
                const msg = this.tooltipValueGetter(params);
                return msg;
            }
        },
        {
            headerName: 'Data Set Name',
            field: 'name',
            automationId: this.modalAutomationId,
            lockVisible: true,
            tooltipValueGetter: (params) => {
                const msg = this.tooltipValueGetter(params);
                return msg;
            }
        },
        {
            headerName: 'Originating Project',
            field: 'owner',
            automationId: this.modalAutomationId,
            lockVisible: true,
            tooltipValueGetter: (params) => {
                const msg = this.tooltipValueGetter(params);
                return msg;
            }
        },
        {
            headerName: 'Parsing Library Version',
            automationId: this.modalAutomationId,
            lockVisible: true,
            valueGetter: function tokenNameGetter(params: any) {
                const externalParsingLibraryVersion = params?.data?.externalParsingLibrary === 'OIT' ? 'V1' : 'V2';
                return externalParsingLibraryVersion;
            },
            tooltipValueGetter: (params) => {
                const externalParsingLibraryVersion = params?.data?.externalParsingLibrary === 'OIT' ? 'V1' : 'V2';
                const msg = this.tooltipValueGetter(params);
                return msg;
            }
        },
        {
            headerName: 'Tokenization Version',
            field: 'tokenizerConfigSchemeDisplay',
            automationId: this.modalAutomationId,
            width: 155,
            suppressSizeToFit: true,
            lockVisible: true,
            tooltipValueGetter: (params) => {
                const msg = this.tooltipValueGetter(params);
                return msg;
            }
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.modalAutomationId,
            lockVisible: true,
            tooltipValueGetter: (params) => {
                const msg = this.tooltipValueGetter(params);
                return msg;
            }
        }
    );
    rowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_listSharedCorpora,
        responseName: 'corpora',
        data: {
            requestHandle: this.commonService.currentOrganizationHandle,
            projectHandle: this.commonService.currentProject.handle,
            organizationName: this.commonService.currentProject.orgName
        }
    });

    rowClassRules = {
        'disabled-row': (params) => {
            return (params && params.data instanceof Corpus && (params.data as Corpus).tokenizerConfigSchemeDisplay !== this.commonService.tokenizerConfigVersion)
                || (this.commonService.externalParsingLibrary !== params.data.externalParsingLibrary);
        }
    };
    responseArrayCallback = (value: any) => {
        if (!this.hasDisabledRow) {
            this.hasDisabledRow = !this.isRowSelectable({ data: value })
        }
        return new Corpus(value);
    }
    isRowSelectable = (node) => {
        return !((node && node.data instanceof Corpus && (node.data as Corpus).tokenizerConfigSchemeDisplay !== this.commonService.tokenizerConfigVersion)
            || (this.commonService.externalParsingLibrary !== node.data.externalParsingLibrary));
    }

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        this.hasAddToProjectPermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] }]);
    }

    tooltipValueGetter(params): string {
        return (params && params.data instanceof Corpus && (params.data as Corpus).tokenizerConfigSchemeDisplay !== this.commonService.tokenizerConfigVersion)
            || (this.commonService.externalParsingLibrary !== params.data.externalParsingLibrary) ? 'Parsing Library Version does not match your Project' : '';
    }

    selectedRows(): Corpus[] {
        if (this.genericGrid?.gridApi) {
            return this.genericGrid.gridApi.getSelectedRows().filter(row => row.tokenizerConfigSchemeDisplay === this.commonService.tokenizerConfigVersion);
        }
        return [];
    }

    isPrimaryButtonEnabled(): boolean {
        return this.selectedRows().length > 0 && !this.isBusy;
    }

    addSharedDataSet(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Adding Shared Data Set...';
        const selectedRows = this.selectedRows();
        if (selectedRows.length > 0) {
            const selectedHandles = selectedRows.map(row => row.corpusViewHandle);
            this.restService.post(Endpoint.projectManager_attachCorpusToProject, {
                addToCaseData: this.addToProjectData,
                projectHandle: this.commonService.currentProject.handle,
                corpusHandleList: selectedHandles,
                workingSetHandle: this.commonService.projectData.handle
            }).then(response => {
                if (response) {
                    this.isBusy = false;
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CORPUS);
                    const selectedDataSet = this.genericGrid.getData().find((item) => item.corpusViewHandle === selectedHandles[0]);
                    this.closeModal(true, Object.assign(selectedDataSet, { entityType: UpdateStatusType.CORPUS }));
                }
            }, error => {
                this.isBusy = false;
            });
        }
    }
}
